import React, { useState } from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  width?: number;
  height?: number;
  loading?: 'lazy' | 'eager';
  priority?: boolean;
  fallbackSrc?: string;
  title?: string; 
  sizes?: string;
  srcSet?: string;
} 

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className = '',
  style,
  width,
  height,
  loading = 'lazy',
  priority = false,
  fallbackSrc = "/home/project/public/hero.webp",  
  title,
  sizes,
  srcSet
}) => {
  const [imgSrc, setImgSrc] = useState(src);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoading(false);
    console.log('Image failed to load:', imgSrc);
    if (imgSrc !== fallbackSrc) {
      setImgSrc(fallbackSrc);
      setHasError(false); // Reset error state when switching to fallback
    }
  };

  // Use original src without WebP conversion for better compatibility
  const finalSrc = imgSrc;
  
  return (
    <div className={`relative ${className}`} style={style}>
      {isLoading && (
        <div 
          className="absolute inset-0 bg-gray-200 animate-pulse rounded"
          style={{ width, height }}
        >
          <div className="flex items-center justify-center h-full">
            <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
          </div>
        </div>
      )}
      <img
        src={finalSrc}
        srcSet={srcSet}
        sizes={sizes || "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"}
        alt={alt}
        title={title || alt}
        width={width}
        height={height}
        loading={priority ? 'eager' : loading}
        decoding="async"
        className={`${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300 ${className}`}
        style={style}
        onLoad={handleLoad}
        onError={handleError}
        // SEO and accessibility attributes
        role="img"
        aria-label={alt}
      />
      {hasError && imgSrc === fallbackSrc && (
        <div 
          className="absolute inset-0 bg-gray-100 flex items-center justify-center text-gray-500 text-sm"
          style={{ width, height }}
        >
          <span>Image not available</span>
        </div>
      )}
    </div>
  );
};

export default OptimizedImage;